import {
  GlobalOrganizationsLabel,
  replaceOrganizationLabel,
} from 'features/global-organizations-setting-state/global-organizations-setting.constant';
import _get from 'lodash-es/get';
import { required, withMessage } from '@certemy/common';
import { SmartFilters, SmartForm } from '@certemy/ui-components';
import { FormControl } from '@angular/forms';

export enum LOCATION_TYPES {
  COUNTRY = 1,
  REGION,
  OFFICE,
}

export enum REGION_TYPES {
  COUNTRY = 1,
  STATE,
  CUSTOM,
}

export const REGION_TYPE_NAMES = {
  [REGION_TYPES.COUNTRY]: 'Country',
  [REGION_TYPES.STATE]: 'State',
  [REGION_TYPES.CUSTOM]: 'Custom',
};

export const LOCATION_TYPES_ARR_NAME = {
  [LOCATION_TYPES.COUNTRY]: 'countries',
  [LOCATION_TYPES.REGION]: 'regions',
  [LOCATION_TYPES.OFFICE]: 'offices',
};

export interface LocationTypesArrayValue {
  ids: number[];
  names: string[];
}

export interface LocationBasicData {
  id: number;
  name: string;
}

export interface OfficeData {
  office: LocationBasicData;
  region: LocationBasicData;
  country: LocationBasicData;
}

export interface LocationTypesCountryDictionaryItem extends SmartFilters.FilterOption {}
export interface LocationTypesRegionDictionaryItem extends SmartFilters.FilterOption {
  countryId?: number;
}
export interface LocationTypesOfficeDictionaryItem extends SmartFilters.FilterOption {
  regionId?: number;
}

export interface LocationTypesCountryArrayValue extends LocationTypesArrayValue {
  dictionaryItems: LocationTypesCountryDictionaryItem[];
  idToLocationObjectMap: { [key in string]: LocationTypesCountryDictionaryItem };
}
export interface LocationTypesRegionArrayValue extends LocationTypesArrayValue {
  dictionaryItems: LocationTypesRegionDictionaryItem[];
  idToLocationObjectMap: { [key in string]: LocationTypesRegionDictionaryItem };
}
export interface LocationTypesOfficeArrayValue extends LocationTypesArrayValue {
  dictionaryItems: LocationTypesOfficeDictionaryItem[];
  idToLocationObjectMap: { [key in string]: LocationTypesOfficeDictionaryItem };
}

export interface LocationTypesResponse {
  countries: LocationTypesCountryArrayValue;
  regions: LocationTypesRegionArrayValue;
  offices: LocationTypesOfficeArrayValue;
  firstOffice?: OfficeData;
}

export const LOCATION_TYPES_FIRST_OFFICE_KEYS = {
  [LOCATION_TYPES.COUNTRY]: 'country',
  [LOCATION_TYPES.REGION]: 'region',
  [LOCATION_TYPES.OFFICE]: 'office',
};

export const REGION_TYPE_ORDER = {
  [REGION_TYPES.STATE]: 0,
  [REGION_TYPES.COUNTRY]: 1,
  [REGION_TYPES.CUSTOM]: 2,
};

export interface LocationRegionPayload {
  organizationCountryId: number;
  regionTypeId: REGION_TYPES;
  stateId: number;
  regionName: string;
}

export interface Country {
  id: number;
  name: string;
  code: string;
}

export interface RegionDictionaryItem {
  id: number;
  name: string;
  regionTypeId: REGION_TYPES;
  stateId: number;
}

export interface LocationCountryPayload {
  countryId: number;
}

export interface Office {
  organizationCountryId: number;
  organizationRegionId: number;
  name: string;
  address1: string;
  address2: string;
  city: string;
  stateId: number;
  stateName: string;
  postalCode: string;
  regionTypeId?: REGION_TYPES;
}

export interface OfficeForm {
  organizationCountryId: FormControl<number>;
  organizationRegionId: FormControl<number>;
  name: FormControl<string>;
  address1: FormControl<string>;
  address2: FormControl<string>;
  city: FormControl<string>;
  stateId: FormControl<number>;
  stateName: FormControl<string>;
  postalCode: FormControl<string>;
}

export interface Region {
  organizationRegionId: number;
  organizationCountryId: number;
  regionTypeId: REGION_TYPES;
  organizationRegionStateId: number;
  regionName: string;
}

export interface RegionForm {
  organizationCountryId: FormControl<number>;
  regionTypeId: FormControl<REGION_TYPES>;
  stateId: FormControl<number>;
  regionName: FormControl<string>;
}

export const ONLY_STATE_TYPE_REGION_COUNTRY = [1, 2];

export const INITIAL_ORGANIZATION_OFFICE: Office = {
  organizationCountryId: null,
  name: null,
  city: null,
  address1: null,
  postalCode: null,
  organizationRegionId: null,
  address2: null,
  stateId: null,
  stateName: null,
};

export interface OrganizationLabelAdditionalOptions {
  activeProfileAvailable: boolean;
  existAssociatedProfile: boolean;
  activeProfessionalsNumber: number;
  activeAdminsNumber: number;
  location: string;
  action: string;
}

export function replaceLocationOrganizationLabel(
  name,
  labels: GlobalOrganizationsLabel,
  options?: Partial<OrganizationLabelAdditionalOptions>,
) {
  return replaceOrganizationLabel(name, labels)
    .replace(/{activeProfessionalsNumber}/g, _get(options, 'activeProfessionalsNumber[0]', ''))
    .replace(/{activeAdminsNumber}/g, _get(options, 'activeAdminsNumber[0]', ''))
    .replace(/{location}/g, _get(options, 'location', ''))
    .replace(/{action}/g, _get(options, 'action', ''));
}

export enum LOCATION_VALIDATION_RULES_ACTIONS {
  ADD = 1,
  EDIT = 2,
  CHANGE = 3,
}

export const LOCATION_VALIDATION_RULES = {
  [LOCATION_TYPES.COUNTRY]: {
    [LOCATION_VALIDATION_RULES_ACTIONS.ADD]:
      'WARNING: If you add a new country, please note that if you want to associate that country to existing {country_manager} or {executive} users, you will need to go to their profiles and add the country to those profiles.',
    [LOCATION_VALIDATION_RULES_ACTIONS.EDIT]:
      'WARNING: There are {activeProfessionalsNumber} employees and {activeAdminsNumber} administrators already associated with this country. Please contact support@certemy.com to remove this country. Alternatively, you may update the countries associated with those users first and then remove this country after.',
  },
  [LOCATION_TYPES.REGION]: {
    [LOCATION_VALIDATION_RULES_ACTIONS.ADD]:
      'WARNING: If you add a new {region} in this country, please note that if you want to associate that {region} to existing {country_manager} or {executive} users, you will need to go to their profiles and add the {region} to those profiles.',
    [LOCATION_VALIDATION_RULES_ACTIONS.EDIT]:
      'WARNING: There are {activeProfessionalsNumber} employees and {activeAdminsNumber} administrators already associated with this {region}. Please contact support@certemy.com to remove this {region} or to change the country associated with this region. Alternatively, you may update the {region}(s) associated with those users first and then remove this {region} after.',
    [LOCATION_VALIDATION_RULES_ACTIONS.CHANGE]:
      'Please note that you are changing the country this {region} is associated with. It may take up to 24 hours for this change to appear for all users in your organization.',
  },
  [LOCATION_TYPES.OFFICE]: {
    [LOCATION_VALIDATION_RULES_ACTIONS.ADD]:
      'WARNING: If you add a new {office} in this {region}, please note that if you want to associate that {office} to existing {regional_manager} or {country_manager} or {executive} users, you will need to go to their profiles and add the {office} to those profiles.',
    [LOCATION_VALIDATION_RULES_ACTIONS.EDIT]:
      'WARNING: There are {activeProfessionalsNumber} employees and {activeAdminsNumber} administrators already associated with this {office}. Please contact support@certemy.com to remove this {office} or to change the {region} or country associated with this {office}. Alternatively, you may either add a new {office} or update the countries and {region}(s) associated with those users.',
    [LOCATION_VALIDATION_RULES_ACTIONS.CHANGE]:
      'Please note that you are changing the {region} this {office} is associated with. It may take up to 24 hours for this change to appear for all users in your organization.',
  },
};

export const LOCATION_VALIDATION_FORCIBLY_SHOW_WARNING = {
  [LOCATION_TYPES.OFFICE]: {
    [LOCATION_VALIDATION_RULES_ACTIONS.ADD]: true,
  },
};

export const ORGANIZATION_LOCATION_VALIDATION_MESSAGES = {
  REQUIRED: 'Please provide information in all required fields',
  POSTAL_CODE: 'Please enter a valid postal code',
  EMPTY_COUNTRY: 'Please select one country.',
  EMPTY_REGION: 'Please select one {region}.',
  EMPTY_OFFICE: 'Please select one {office}.',
  EMPTY_ADDITIONAL_REGION:
    'You have indicated that this employee works in additional locations. You must select at least one additional {region} and one additional {office}.',
  EMPTY_ADDITIONAL_OFFICE:
    'You have indicated that this employee works in additional locations. You must select at least one additional {office}.',
  EQUAL_ADDITIONAL_OFFICE:
    'You have selected an additional {office} that is the same as the employee’s primary {office}. Please select a different additional {office}.',
};

export const requiredWithMessage = withMessage(ORGANIZATION_LOCATION_VALIDATION_MESSAGES.REQUIRED, required);

export enum COUNTRIES_CODE {
  UNITED_STATE = 'US',
  CANADA = 'CA',
}

export interface CountryFieldOption extends SmartForm.FieldOption {
  code?: string;
}

export const LOCATION_VALIDATION_ERROR = 'location_validation_error';

export const OFFICE_SUCCESSFUL_UPDATE =
  'Please note that you will need to log out and then log back in to see these changes. Please note that for other users in your organization, it may take up to 24 hours for them to see the changes. In addition, if you updated the address information for this location, its position on the map (in Reports) may take up to 24 hours to update.';
export const REGION_SUCCESSFUL_UPDATE =
  'Please note that you will need to log out and then log back in to see these changes. Please note that for other users in your organization, it may take up to 24 hours for them to see the changes.';
